import React from 'react'
import {
  Settings,
  Palette,
  Type,
  Image,
  Upload,
  Plug,
  Download,
  Monitor,
  Smartphone,
  SlidersHorizontal,
  BarChart3,
  Sparkles,
  PaintBucket,
  Eye,
  Save,
  RotateCcw,
  ArrowRight,
  FileText,
  Webhook,
  Mail,
} from 'lucide-react'

// ─── Section Wrapper ─────────────────────────────────────────────────────────
function Section({ number, title, children }) {
  return (
    <section className="space-y-4" data-section-number={number}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{number}</span>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      {children}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Page Header with Tabs
// ─────────────────────────────────────────────────────────────────────────────
function PageHeaderMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Settings</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Manage your organisation's branding and data display preferences.</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-8 px-2.5 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center gap-1.5 bg-white dark:bg-gray-800">
            <RotateCcw className="h-3 w-3 text-gray-500" />
            <span className="text-[10px] text-gray-600 dark:text-gray-300">Reset</span>
          </div>
          <div className="h-8 px-3 bg-navy-800 rounded-lg flex items-center gap-1.5">
            <Save className="h-3 w-3 text-white" />
            <span className="text-[10px] text-white font-medium">Save Changes</span>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-6">
          <div className="flex items-center gap-1.5 pb-2.5 border-b-2 border-blue-600">
            <Palette className="h-3.5 w-3.5 text-blue-600" />
            <span className="text-[11px] font-medium text-blue-600">Branding & Appearance</span>
          </div>
          <div className="flex items-center gap-1.5 pb-2.5 border-b-2 border-transparent">
            <SlidersHorizontal className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Data Display</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Identity & Brand Colour Cards
// ─────────────────────────────────────────────────────────────────────────────
function IdentityBrandMockup() {
  return (
    <div className="grid grid-cols-2 gap-4 max-w-xl">
      {/* Identity card */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-3">
        <div className="flex items-center gap-1.5">
          <Type className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Identity</span>
        </div>
        <div>
          <label className="text-[9px] font-medium text-gray-500 dark:text-gray-400 block mb-1">Application Name</label>
          <div className="h-8 px-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
            <span className="text-[10px] text-gray-900 dark:text-gray-100">Xtract</span>
          </div>
        </div>
        <div>
          <label className="text-[9px] font-medium text-gray-500 dark:text-gray-400 block mb-1">Subtitle</label>
          <div className="h-8 px-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
            <span className="text-[10px] text-gray-900 dark:text-gray-100">Synapx AI</span>
          </div>
        </div>
      </div>

      {/* Brand Colour card */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-3">
        <div className="flex items-center gap-1.5">
          <Palette className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Brand Colour</span>
        </div>
        <p className="text-[9px] text-gray-500 dark:text-gray-400">Used for sidebar, buttons, and login page.</p>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-[9px] text-gray-500 dark:text-gray-400 block mb-1">Colour</label>
            <div className="w-9 h-9 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-[#1e2a3b]" />
          </div>
          <div>
            <label className="text-[9px] text-gray-500 dark:text-gray-400 block mb-1">Hex Code</label>
            <div className="h-9 w-24 px-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
              <span className="text-[10px] font-mono text-gray-900 dark:text-gray-100">#1e2a3b</span>
            </div>
          </div>
          <div className="h-9 px-2.5 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center gap-1">
            <Palette className="h-3 w-3 text-gray-500" />
            <span className="text-[9px] text-gray-600 dark:text-gray-300">Palette</span>
          </div>
        </div>
        <div className="flex gap-1.5 pt-1">
          <div className="h-5 px-2.5 rounded-md text-[8px] font-medium text-white flex items-center" style={{ backgroundColor: '#1e2a3b' }}>Button</div>
          <div className="h-5 w-10 rounded-md" style={{ backgroundColor: '#1e2a3b', opacity: 0.6 }} />
          <div className="h-5 w-6 rounded-md" style={{ backgroundColor: '#1e2a3b', opacity: 0.3 }} />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Images Section (Logo & Favicon upload)
// ─────────────────────────────────────────────────────────────────────────────
function ImagesMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-3 max-w-md">
      <div className="flex items-center gap-1.5">
        <Image className="h-3.5 w-3.5 text-gray-400" />
        <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Images</span>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {/* Logo */}
        <div>
          <label className="text-[9px] font-medium text-gray-500 dark:text-gray-400 block mb-1.5">Logo</label>
          <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-4 text-center cursor-default">
            <Upload className="h-5 w-5 text-gray-400 mx-auto" />
            <span className="text-[9px] text-gray-400 block mt-1">Click or drag an image</span>
            <span className="text-[8px] text-gray-300 dark:text-gray-500 block">PNG, JPG, SVG, WebP</span>
          </div>
        </div>
        {/* Favicon */}
        <div>
          <label className="text-[9px] font-medium text-gray-500 dark:text-gray-400 block mb-1.5">Favicon</label>
          <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-4 text-center cursor-default">
            <Upload className="h-5 w-5 text-gray-400 mx-auto" />
            <span className="text-[9px] text-gray-400 block mt-1">Click or drag an image</span>
            <span className="text-[8px] text-gray-300 dark:text-gray-500 block">PNG, JPG, SVG, WebP</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Outlook Plugin Section
// ─────────────────────────────────────────────────────────────────────────────
function OutlookPluginMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-4 max-w-lg">
      <div className="flex items-center gap-1.5">
        <Plug className="h-3.5 w-3.5 text-gray-400" />
        <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Outlook Plugin</span>
      </div>

      {/* Toggle */}
      <div className="flex items-center gap-3">
        <div className="relative w-7 h-4 rounded-full bg-[var(--token-success,#3b9b5e)] flex-shrink-0">
          <div className="absolute top-0.5 right-0.5 w-3 h-3 rounded-full bg-white shadow-sm" />
        </div>
        <div>
          <p className="text-[10px] font-medium text-gray-700 dark:text-gray-300">Use webapp branding for plugin</p>
          <p className="text-[9px] text-gray-500 dark:text-gray-400">The plugin mirrors your webapp name, logo, and colour.</p>
        </div>
      </div>

      {/* Custom fields (when toggle is off) */}
      <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3">
        <p className="text-[9px] text-gray-400 italic">When toggle is off, separate plugin branding fields appear:</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[9px] font-medium text-gray-500 dark:text-gray-400 block mb-1">Plugin Display Name</label>
            <div className="h-7 px-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
              <span className="text-[9px] text-gray-400">e.g. Custom Name</span>
            </div>
          </div>
          <div>
            <label className="text-[9px] font-medium text-gray-500 dark:text-gray-400 block mb-1">Plugin Primary Colour</label>
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-blue-600" />
              <div className="h-7 w-20 px-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
                <span className="text-[9px] font-mono text-gray-400">#1946FA</span>
              </div>
            </div>
          </div>
        </div>
        <div>
          <label className="text-[9px] font-medium text-gray-500 dark:text-gray-400 block mb-1">Body Text</label>
          <div className="h-12 px-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-start pt-2">
            <span className="text-[9px] text-gray-400">Select the email attachments to upload and extract.</span>
          </div>
        </div>
      </div>

      {/* Manifest download */}
      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
            <Download className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold text-gray-900 dark:text-gray-100">Download Manifest</p>
            <p className="text-[9px] text-gray-500 dark:text-gray-400">Generate a branded <code className="text-[8px] bg-gray-100 dark:bg-gray-800 px-0.5 rounded font-mono">manifest.xml</code> for Microsoft 365 Admin Center.</p>
          </div>
          <div className="h-7 px-2.5 bg-navy-800 rounded-lg flex items-center gap-1">
            <Download className="h-3 w-3 text-white" />
            <span className="text-[9px] text-white font-medium">Download</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Live Preview Panel (Sidebar + Plugin)
// ─────────────────────────────────────────────────────────────────────────────
function LivePreviewMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-4 max-w-md">
      <div className="flex items-center gap-1.5">
        <Eye className="h-3.5 w-3.5 text-gray-400" />
        <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Live Preview</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Sidebar preview */}
        <div>
          <div className="flex items-center gap-1 mb-2">
            <Monitor className="h-3 w-3 text-gray-400" />
            <span className="text-[9px] font-medium text-gray-500 dark:text-gray-400">Webapp Sidebar</span>
          </div>
          <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="p-3 space-y-1.5" style={{ backgroundColor: '#1e2a3b' }}>
              <div className="flex items-center gap-2 pb-2 border-b border-white/10">
                <div className="w-5 h-5 rounded bg-white/20" />
                <div>
                  <p className="text-[9px] font-semibold text-white">Xtract</p>
                  <p className="text-[7px] text-white/50">Synapx AI</p>
                </div>
              </div>
              {['Dashboard', 'Email Queue', 'Requests', 'Analyzers', 'Settings'].map((item, i) => (
                <div key={item} className={`px-2 py-1 rounded text-[8px] font-medium ${i === 0 ? 'bg-white/15 text-white' : 'text-white/40'}`}>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Plugin preview */}
        <div>
          <div className="flex items-center gap-1 mb-2">
            <Smartphone className="h-3 w-3 text-gray-400" />
            <span className="text-[9px] font-medium text-gray-500 dark:text-gray-400">Outlook Plugin</span>
          </div>
          <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="px-3 py-2 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center gap-1.5">
              <div className="w-5 h-5 rounded bg-blue-100 dark:bg-blue-900/30" />
              <span className="text-[9px] font-bold text-gray-900 dark:text-gray-100">Xtract</span>
            </div>
            <div className="p-3 space-y-1.5">
              <p className="text-[8px] text-gray-500">Select attachments to process and click Submit.</p>
              {['Document.pdf', 'Invoice.pdf'].map((name, i) => (
                <div key={i} className="flex items-center gap-1.5 rounded px-2 py-1.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                  <div className="w-3 h-3 rounded border border-gray-300 dark:border-gray-600" />
                  <span className="text-[8px] font-medium text-gray-700 dark:text-gray-200">{name}</span>
                </div>
              ))}
              <div className="h-6 rounded text-[8px] font-semibold text-white flex items-center justify-center" style={{ backgroundColor: '#1e2a3b' }}>
                Submit
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Data Display Tab - Toggle Controls
// ─────────────────────────────────────────────────────────────────────────────
function DataDisplayControlsMockup() {
  const toggles = [
    { label: 'Confidence Score', description: 'Show the extraction confidence percentage next to each field value.', icon: BarChart3, on: true },
    { label: 'AI Label', description: 'Display an "AI" badge when a field was extracted using an LLM fallback.', icon: Sparkles, on: true },
    { label: 'Colour Coding', description: 'Colour-coded indicators (green/amber/red) based on extraction confidence level.', icon: PaintBucket, on: false },
  ]
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-3 max-w-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Display Preferences</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] text-gray-500">All</span>
          <div className="relative w-8 h-4 rounded-full bg-gray-200 dark:bg-gray-700">
            <div className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow-sm" />
          </div>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40">
        <span className="text-blue-500 text-[10px]">ℹï¸</span>
        <p className="text-[9px] text-blue-700 dark:text-blue-300">These preferences control how extracted data is displayed across all request views.</p>
      </div>

      <div className="space-y-2">
        {toggles.map(toggle => {
          const Icon = toggle.icon
          return (
            <div key={toggle.label} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 cursor-default">
              <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                <Icon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[10px] font-medium text-gray-900 dark:text-gray-100 block">{toggle.label}</span>
                <span className="text-[9px] text-gray-500 dark:text-gray-400">{toggle.description}</span>
              </div>
              <div className={`relative w-7 h-4 rounded-full flex-shrink-0 ${toggle.on ? 'bg-[var(--token-success,#3b9b5e)]' : 'bg-gray-200 dark:bg-gray-700'}`}>
                <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-sm ${toggle.on ? 'right-0.5' : 'left-0.5'}`} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Data Display Live Preview (Fields with confidence badges)
// ─────────────────────────────────────────────────────────────────────────────
function DataDisplayPreviewMockup() {
  const fields = [
    { name: 'Invoice Number', value: 'INV-2026-00142', confidence: 96, source: 'doc', color: 'green' },
    { name: 'Vendor Name', value: 'Acme Corporation Ltd', confidence: 78, source: 'ai', color: 'orange' },
    { name: 'Due Date', value: '2026-06-15', confidence: 65, source: 'ai', color: 'red' },
    { name: 'Total Amount', value: 'GBP 12,450.00', confidence: 92, source: 'doc', color: 'green' },
  ]
  const colorMap = {
    green: { dot: 'bg-green-500', badge: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', border: 'border-gray-200 dark:border-gray-600' },
    orange: { dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', border: 'border-orange-300 dark:border-orange-700' },
    red: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', border: 'border-red-300 dark:border-red-700' },
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden max-w-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex items-center gap-2">
        <div className="w-6 h-6 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <FileText className="w-3 h-3 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <p className="text-[10px] font-semibold text-gray-900 dark:text-gray-100">Request #275</p>
          <p className="text-[9px] text-gray-500 dark:text-gray-400">Extracted Fields</p>
        </div>
      </div>
      {/* Fields */}
      <div className="p-4 space-y-3">
        {fields.map((field, i) => {
          const cm = colorMap[field.color]
          return (
            <div key={i}>
              <div className="flex items-center mb-1 gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${cm.dot}`} />
                <span className="text-[10px] font-medium text-gray-600 dark:text-gray-400">{field.name}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded ${cm.badge}`}>{field.confidence}%</span>
                {field.source === 'ai' && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">AI</span>
                )}
                {field.color === 'red' && (
                  <span className="text-[9px] text-red-500 italic">Low confidence</span>
                )}
              </div>
              <div className={`w-full px-2.5 py-1.5 text-[10px] font-medium rounded-lg border ${cm.border} ${field.color === 'red' ? 'bg-red-50 dark:bg-red-900/20' : field.color === 'orange' ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-white dark:bg-gray-800'} text-gray-900 dark:text-gray-200`}>
                {field.value}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Colour Palette Grid
// ─────────────────────────────────────────────────────────────────────────────
function PaletteGridMockup() {
  const colors = [
    '#450a0a','#991b1b','#b91c1c','#800020','#5c2d0e',
    '#9a3412','#854d0e','#a16207','#166534','#1b4332',
    '#115e59','#003366','#0c2340','#1e3a8a','#172554',
    '#4c1d95','#3730a3','#86198f','#881337','#0f172a',
  ]
  return (
    <div className="inline-block p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div className="grid grid-cols-10 gap-1">
        {colors.map((color) => (
          <div key={color} className="w-5 h-5 rounded-md ring-1 ring-black/10 dark:ring-white/10" style={{ backgroundColor: color }} />
        ))}
      </div>
    </div>
  )
}

// ===========================================================================
// SETTINGS GUIDE
// ===========================================================================
export default function SettingsGuide({ onNavigate }) {
  return (
    <div className="space-y-10">
      {/* Module header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
          <Settings className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Settings</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Configure your organisation's branding (name, colour, logo, favicon, plugin appearance) and control how extracted data is displayed across request views.
          </p>
        </div>
      </div>

      {/* Section 1: Page Header & Tabs */}
      <Section number={1} title="Page Header & Tabs">
        <PageHeaderMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The Settings page has a header with title, description, and action buttons. Two tabs control which settings panel is shown:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Branding & Appearance</strong> - Brand colour, identity, images, and Outlook plugin settings. Shows "Reset" and "Save Changes" buttons.</li>
          <li><strong>Data Display</strong> - Controls for confidence scores, AI badges, and colour coding. Shows "Discard" (if changes exist) and "Save Changes" buttons.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The active tab has a blue underline and icon colour. The "Save Changes" button is disabled when there are no pending changes on the Data Display tab.
        </p>
      </Section>

      {/* Section 2: Identity & Brand Colour */}
      <Section number={2} title="Identity & Brand Colour">
        <IdentityBrandMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Two side-by-side cards on the Branding tab:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Identity</strong> - Set the Application Name (shown in sidebar header, browser title) and Subtitle (shown below the name in the sidebar).</li>
          <li><strong>Brand Colour</strong> - Pick a hex colour using the native colour picker, type a hex code manually, or click "Palette" to reveal a quick-pick grid of pre-selected colours (100+ options, scrollable).</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          A small preview swatch shows the colour applied to a button, 60% opacity, and 30% opacity so you can see how it will look across the UI.
        </p>
      </Section>

      {/* Section 3: Quick-Pick Palette */}
      <Section number={3} title="Quick-Pick Colour Palette">
        <PaletteGridMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Click "Palette" to expand a 10-column scrollable grid of 100+ curated dark colours. Each swatch is a button - clicking it sets the brand colour immediately and closes the palette. The currently active colour gets a blue ring. Colours range from reds, oranges, greens, blues, purples, and neutrals - all chosen to work well as sidebar backgrounds and accent colours.
        </p>
      </Section>

      {/* Section 4: Images (Logo & Favicon) */}
      <Section number={4} title="Images (Logo & Favicon)">
        <ImagesMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Two drag-and-drop upload zones side by side:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Logo</strong> - Shown in the sidebar header and plugin. Accepts PNG, JPG, SVG, WebP.</li>
          <li><strong>Favicon</strong> - Browser tab icon. Same formats accepted.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Click the zone or drag a file onto it. Once uploaded, a preview of the image appears inside the zone with "Click or drag to replace" text. The upload happens immediately but you must click <strong>Save Changes</strong> to persist.
        </p>
      </Section>

      {/* Section 5: Outlook Plugin */}
      <Section number={5} title="Outlook Plugin Branding">
        <OutlookPluginMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Controls how the Outlook add-in looks for your users:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Toggle: "Use webapp branding for plugin"</strong> - When ON, the plugin uses the same name, logo, and colour as your webapp. When OFF, separate fields appear below.</li>
          <li><strong>Plugin Display Name</strong> - Shown in the Outlook ribbon and taskpane header.</li>
          <li><strong>Plugin Primary Colour</strong> - Controls the Submit button, footer, and accent elements in the plugin UI.</li>
          <li><strong>Body Text</strong> - Custom instruction text shown in the plugin body (e.g. "Select attachments to upload and extract").</li>
          <li><strong>Plugin Logo</strong> - Upload a separate logo for the plugin (if different from webapp logo).</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mt-2">
          <strong>Download Manifest</strong> - Generates a branded <code className="text-[11px] bg-gray-100 dark:bg-gray-800 px-1 rounded font-mono">manifest.xml</code> file to deploy the plugin via Microsoft 365 Admin Center. It includes your custom name, logo URL, and colours.
        </p>
      </Section>

      {/* Section 6: Live Preview Panel */}
      <Section number={6} title="Live Preview Panel">
        <LivePreviewMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          A sticky right-side panel shows real-time previews as you edit branding settings:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Webapp Sidebar</strong> - Shows how the sidebar will look with your brand colour, logo, app name, subtitle, nav items, and user avatar section.</li>
          <li><strong>Outlook Plugin</strong> - Shows a full plugin taskpane preview with header (logo + name), file list, Submit button, related requests, and footer. Includes a dark/light toggle to preview both modes.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Both previews update instantly as you change any branding field - no save needed to see the preview.
        </p>
      </Section>

      {/* Section 7: Data Display Preferences */}
      <Section number={7} title="Data Display Preferences">
        <DataDisplayControlsMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The Data Display tab has 3 toggle controls plus a master "All" toggle:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Confidence Score</strong> - Shows the extraction confidence percentage (e.g. 96%) as a coloured badge next to each field label.</li>
          <li><strong>AI Label</strong> - Displays a purple "AI" badge when a field was extracted using an LLM fallback instead of Document Intelligence.</li>
          <li><strong>Colour Coding</strong> - Adds colour-coded dots and input borders: green (≥90%), orange (70-89%), red (&lt;70%) plus a "Low confidence" italic warning for red fields.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mt-2">
          The "All" master toggle enables or disables all three at once. An amber banner appears when you have unsaved changes. Changes are drafts until you click <strong>Save Changes</strong> - they sync across all browser tabs via context.
        </p>
      </Section>

      {/* Section 8: Data Display Live Preview */}
      <Section number={8} title="Data Display Live Preview">
        <DataDisplayPreviewMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The right panel shows a mock "Request #275 - Extracted Fields" card that updates in real-time as you toggle preferences:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Confidence badges</strong> animate in/out when the Confidence Score toggle changes.</li>
          <li><strong>AI badges</strong> appear/disappear for LLM-sourced fields.</li>
          <li><strong>Colour dots, borders, and "Low confidence" text</strong> fade in/out with the Colour Coding toggle.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The preview shows a "Draft" badge when current settings differ from saved. This gives you confidence in what the changes will look like before saving.
        </p>
      </Section>

      {/* Tips */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Palette className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">Dark Colours Work Best</span>
            </div>
            <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">
              The sidebar and plugin footer use your brand colour as a solid background with white text. Dark, saturated colours give the best contrast and readability.
            </p>
          </div>
          <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Eye className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-semibold text-purple-900 dark:text-purple-200">Preview Before Saving</span>
            </div>
            <p className="text-[11px] text-purple-700 dark:text-purple-300 leading-relaxed">
              The live preview updates instantly - try different colours and check both the sidebar and plugin before committing with Save Changes.
            </p>
          </div>
          <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Download className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-900 dark:text-green-200">Manifest Deployment</span>
            </div>
            <p className="text-[11px] text-green-700 dark:text-green-300 leading-relaxed">
              After downloading the manifest, deploy it via Microsoft 365 Admin Center → Integrated Apps → Upload Custom App. Your branded plugin will appear in all users' Outlook.
            </p>
          </div>
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <RotateCcw className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">Reset is Permanent</span>
            </div>
            <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">
              The Reset button restores all branding to Xtract defaults - logo, favicon, colours, name, and plugin settings are all removed. A confirmation popup prevents accidental resets.
            </p>
          </div>
        </div>
      </section>

      {/* Related Modules */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Related Modules</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'templates', label: 'Templates', icon: FileText },
            { id: 'webhooks', label: 'Webhooks', icon: Webhook },
            { id: 'requests', label: 'Requests', icon: Mail },
          ].map((mod) => {
            const Icon = mod.icon
            return (
              <button key={mod.label} onClick={() => onNavigate(mod.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <Icon className="h-3.5 w-3.5" />
                {mod.label}
                <ArrowRight className="h-3 w-3 text-gray-400" />
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}

